import { useState } from 'react';
import { FaArrowLeft, FaCheck, FaSearch, FaTimes } from 'react-icons/fa';
import { getPersonId, getPersonName } from '../utils/messengerPeopleUtils';
import { Avatar } from './Avatar';

/**
 * Individual recipient item in the list.
 */
function RecipientItem({ person, onToggle }) {
  const personId = getPersonId(person);
  const displayName = getPersonName(person);

  return (
    <button
      type="button"
      className="responder-messenger-recipient-row"
      onClick={() => onToggle(personId)}
    >
      <Avatar label={displayName} />
      <span className="responder-messenger-recipient-row__name">{displayName}</span>
      <span className="responder-messenger-recipient-row__add">
        <FaCheck size={10} />
      </span>
    </button>
  );
}

/**
 * Chip for selected recipient.
 */
function RecipientChip({ person, onRemove }) {
  const displayName = getPersonName(person);

  return (
    <button type="button" className="responder-messenger-chip" onClick={onRemove}>
      {displayName} <FaTimes size={9} />
    </button>
  );
}

/**
 * Personnel picker for creating conversations.
 * @param {{ personnel: object, selectedIds: number[], onToggleRecipient: (id: number) => void }} props
 */
export function RecipientPicker({ personnel, selectedIds, onToggleRecipient, currentUserId, onCancel }) {
  const [searchQuery, setSearchQuery] = useState('');

  // All personnel are available for messaging except the current user
  const eligiblePersonnel = Object.values(personnel || {})
    .filter((person) => person && `${getPersonId(person)}` !== `${currentUserId}`)
    .sort((a, b) => getPersonName(a).localeCompare(getPersonName(b)));

  // Filter by search query
  const availablePersonnel = eligiblePersonnel.filter((person) => {
    const personId = getPersonId(person);
    if (selectedIds.includes(personId)) return false;
    if (!searchQuery) return true;
    return getPersonName(person).toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Get selected personnel objects
  const selectedPersonnel = selectedIds
    .map((id) => eligiblePersonnel.find((p) => getPersonId(p) === id))
    .filter(Boolean);

  return (
    <div className="responder-messenger-composer">
      <div className="responder-messenger-search">
        {onCancel && (
          <button
            type="button"
            className="responder-messenger-icon-btn"
            aria-label="Cancel"
            onClick={onCancel}
          >
            <FaArrowLeft size={12} />
          </button>
        )}
        <FaSearch size={11} />
        <input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          type="text"
          placeholder="Search eligible personnel"
        />
      </div>

      {selectedPersonnel.length > 0 && (
        <div className="responder-messenger-chip-list">
          {selectedPersonnel.map((person) => (
            <RecipientChip
              key={getPersonId(person)}
              person={person}
              onRemove={() => onToggleRecipient(getPersonId(person))}
            />
          ))}
        </div>
      )}

      <div className="responder-messenger-recipient-list">
        {availablePersonnel.length === 0 ? (
          <div className="responder-messenger-empty">
            {eligiblePersonnel.length === 0
              ? 'No personnel found.'
              : searchQuery
                ? 'No personnel matches that search.'
                : 'All personnel already selected.'}
          </div>
        ) : (
          availablePersonnel.map((person) => (
            <RecipientItem key={getPersonId(person)} person={person} onToggle={onToggleRecipient} />
          ))
        )}
      </div>
    </div>
  );
}
